import {
  type FieldApi,
  FieldRow,
  handleStringChange,
} from '@/components/form/formHelpers'
import type {
  LicenseResponseBody,
  UpdateLicenseMetadataRequest,
} from '@/features/Licenses/api/licenseHooks'
import { useUpdateOrgLicenseMetadata } from '@/features/Licenses/api/licenseHooks'
import { LicenseFormDialog } from '@/features/Licenses/components/LicenseFormDialog'
import {
  canClearLicenseReference,
  hasStripeBilling,
} from '@/features/Licenses/lib/licensePredicates'
import {
  buildMutationOptions,
  mapMutationError,
} from '@/features/Licenses/lib/mutationHandlers'
import { Button, Input, Textarea } from '@chakra-ui/react'
import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

type Props = { license: LicenseResponseBody; onSuccess: () => void }

/**
 * 422 joins the inline statuses because that is what the license-service
 * returns when `reference` is cleared on a non-`self-service` license
 * (`code: REFERENCE_REQUIRED_FOR_ORIGIN`). The client-side validation below
 * should catch that first, so a 422 arriving here means the two disagree —
 * exactly the case the operator needs to read in the dialog beside the field,
 * not in a toast that outlives it.
 */
const INLINE_STATUSES = [400, 422, 502]

export function EditMetadataForm({ license, onSuccess }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()
  const retryRef = useRef<() => void>(() => {
    // replaced after form initialization
  })

  // Reference is an SLG traceability field with no meaning for a Stripe-managed
  // license, so it is hidden outright rather than shown disabled the way
  // EntitlementsPanel handles its self-service restriction. A disabled control
  // implies "this could apply to you but doesn't right now"; here the field
  // simply does not apply, and an always-empty greyed input is noise.
  const hasStripe = hasStripeBilling(license)
  const canClearReference = canClearLicenseReference(license)

  const mutation = useUpdateOrgLicenseMetadata(
    buildMutationOptions({
      queryClient,
      license,
      onSuccess,
      toast: {
        id: 'update-metadata-error',
        title: String(t('licenses.actions.editMetadata.error.title')),
        description: String(t('licenses.actions.editMetadata.error.generic')),
        retry: {
          label: String(t('licenses.actions.editMetadata.error.retry')),
          onClick: () => retryRef.current(),
        },
      },
      inlineStatuses: INLINE_STATUSES,
    }),
  )

  const schema = z
    .object({
      name: z
        .string()
        .trim()
        .max(
          255,
          String(t('licenses.actions.editMetadata.validation.maxName')),
        ),
      description: z
        .string()
        .trim()
        .max(
          1000,
          String(t('licenses.actions.editMetadata.validation.maxDescription')),
        ),
      reference: z
        .string()
        .trim()
        .max(
          255,
          String(t('licenses.actions.editMetadata.validation.maxReference')),
        ),
    })
    .refine(
      (d) =>
        Boolean(d.name?.trim()) ||
        Boolean(d.description?.trim()) ||
        (!hasStripe && Boolean(d.reference?.trim())),
      {
        // Attach to a field path so the form-error renders inside the row;
        // a refine without a path resolves to the form root and never surfaces
        // because LicenseFormDialog doesn't render form-level errors.
        path: ['name'],
        message: String(
          t('licenses.actions.editMetadata.validation.atLeastOne'),
        ),
      },
    )
    .refine(
      (d) => hasStripe || canClearReference || Boolean(d.reference.trim()),
      {
        // The invariant, checked here so the operator sees it the moment the
        // field empties rather than after a round trip. The backend still
        // enforces it — this is a courtesy, not the guarantee.
        path: ['reference'],
        message: String(
          t('licenses.actions.editMetadata.validation.referenceRequired'),
        ),
      },
    )

  const form = useForm({
    defaultValues: {
      name: license.name ?? '',
      description: license.description ?? '',
      reference: license.reference ?? '',
    },
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      // Only send fields the operator actually changed. Sending unchanged
      // values would create no-op audit-log entries and add churn for
      // downstream readers (audit, sales-touched detection, etc.).
      const trimmedName = value.name.trim()
      const trimmedDescription = value.description.trim()
      const body: UpdateLicenseMetadataRequest = {}
      if (trimmedName && trimmedName !== (license.name ?? '')) {
        body.name = trimmedName
      }
      if (
        trimmedDescription &&
        trimmedDescription !== (license.description ?? '')
      ) {
        body.description = trimmedDescription
      }
      // Reference is the one field that can be *cleared*, so it breaks the
      // "only send truthy changes" rule the other two follow: an empty string
      // is a deliberate instruction to remove the value, not an absence. It is
      // sent whenever it differs from the stored value, empty included.
      //
      // Trimming before comparing means a whitespace-only entry becomes a
      // clear. That is deliberate: the backend rejects whitespace-only with a
      // 400 to stop sloppy clients sending ambiguous input, and normalising it
      // to the unambiguous form the operator plainly meant satisfies that
      // intent rather than bouncing them off it.
      if (!hasStripe) {
        const trimmedReference = value.reference.trim()
        if (trimmedReference !== (license.reference ?? '')) {
          body.reference = trimmedReference
        }
      }
      // If the operator submitted with no actual changes, skip the network
      // call entirely — the at-least-one refine guards against empty + empty,
      // but unchanged + unchanged falls through here. Reference is tested
      // against `undefined` rather than falsiness, because a cleared reference
      // is the empty string and is a real change that must be sent.
      if (!body.name && !body.description && body.reference === undefined) {
        onSuccess()
        return
      }
      await mutation
        .mutateAsync({
          pathParams: { orgId: license.orgId, licenseId: license.id },
          body,
        })
        .catch(() => {
          // handled by onError
        })
    },
  })

  retryRef.current = () => void form.handleSubmit()

  const inDialogError = mapMutationError(mutation, {
    inlineStatuses: INLINE_STATUSES,
    correlationIdStatuses: [502],
    fallbackDetail: String(t('licenses.actions.editMetadata.error.generic')),
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <LicenseFormDialog
        title={String(t('licenses.actions.editMetadata.title'))}
        errorTitle={String(t('licenses.actions.editMetadata.error.title'))}
        correlationIdLabel={String(
          t('licenses.actions.editMetadata.error.correlationId'),
        )}
        inDialogError={inDialogError}
        cancelLabel={String(t('licenses.actions.editMetadata.cancel'))}
        submitButton={
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                colorPalette="brand"
                loading={Boolean(isSubmitting) || mutation.isPending}
                disabled={!canSubmit || mutation.isPending}
              >
                {String(t('licenses.actions.editMetadata.submit'))}
              </Button>
            )}
          </form.Subscribe>
        }
      >
        <form.Field name="name">
          {(field) => (
            <FieldRow
              field={field as FieldApi<unknown>}
              label={String(
                t('licenses.actions.editMetadata.fields.name.label'),
              )}
            >
              <Input
                placeholder={String(
                  t('licenses.actions.editMetadata.fields.name.placeholder'),
                )}
                value={field.state.value}
                onChange={handleStringChange(field)}
                onBlur={field.handleBlur}
              />
            </FieldRow>
          )}
        </form.Field>
        <form.Field name="description">
          {(field) => (
            <FieldRow
              field={field as FieldApi<unknown>}
              label={String(
                t('licenses.actions.editMetadata.fields.description.label'),
              )}
            >
              <Textarea
                placeholder={String(
                  t(
                    'licenses.actions.editMetadata.fields.description.placeholder',
                  ),
                )}
                value={field.state.value}
                onChange={handleStringChange(field as FieldApi<string>)}
                onBlur={field.handleBlur}
                rows={3}
              />
            </FieldRow>
          )}
        </form.Field>
        {hasStripe ? null : (
          <form.Field name="reference">
            {(field) => (
              <FieldRow
                field={field as FieldApi<unknown>}
                label={String(
                  t('licenses.actions.editMetadata.fields.reference.label'),
                )}
                helperText={String(
                  canClearReference
                    ? t('licenses.actions.editMetadata.fields.reference.helper')
                    : t(
                        'licenses.actions.editMetadata.fields.reference.helperRequired',
                      ),
                )}
              >
                <Input
                  placeholder={String(
                    t(
                      'licenses.actions.editMetadata.fields.reference.placeholder',
                    ),
                  )}
                  value={field.state.value}
                  onChange={handleStringChange(field)}
                  onBlur={field.handleBlur}
                />
              </FieldRow>
            )}
          </form.Field>
        )}
      </LicenseFormDialog>
    </form>
  )
}
